import React, { useEffect, useState } from "react";

export default function ListUsers() {
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [search, setSearch] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const USERS_PER_PAGE = 5;

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setPage(1);
            setSearch(searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    useEffect(() => {
        fetchUsers(page, search);
    }, [page, search]);

    const fetchUsers = async (currentPage, search) => {
        setLoading(true);
        setError("");
        setMessage(""); // ✅ Clear any previous messages

        if (currentPage === 1) {
            setUsers([]);
        }
        const token = sessionStorage.getItem("token");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BASE_URL}cc/r/users?page=${currentPage}&limit=${USERS_PER_PAGE}&search=${encodeURIComponent(search)}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                }
            );

            if (!res.ok) throw new Error(`Server error: ${res.status}`);

            const data = await res.json();

            if (!Array.isArray(data.users)) {
                throw new Error("Invalid response");
            }

            setUsers((prev) =>
                currentPage === 1 ? data.users : [...prev, ...data.users]
            );
            setHasMore(data.hasMore);
        } catch (err) {
            setError("Failed to load users. Please try again.");
        }

        setLoading(false);
    };


    const toggleUserStatus = async (username, currentStatus) => {
        const newStatus = currentStatus === "active" ? 0 : 1;
        const token = sessionStorage.getItem("token");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BASE_URL}cc/r/users/toggle-status`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        username: username,
                        is_active: newStatus,
                    }),
                }
            );

            if (!res.ok) throw new Error("Failed to update user status");


            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.user === username
                        ? {
                            ...user,
                            status: newStatus === 1 ? "active" : "inactive",
                        }
                        : user
                )
            );

            setMessage(`User "${username}" has been ${newStatus === 1 ? "activated" : "deactivated"}.`);
        } catch (err) {
            setMessage("Error updating user status, please try again.");
        }
    };

    const handleRetry = () => {
        fetchUsers(page, search);
    };

    return (
        <div>
            <h2 className="text-center mb-2 mt-4">Edit Users</h2>


            {message && (
                <div className="text-center mb-3">
                    <div
                        className={`p-2 rounded ${message.toLowerCase().includes("error")
                            ? "bg-danger text-white"
                            : "bg-success text-white"
                            }`}
                    >
                        {message}
                    </div>
                </div>
            )}

            {/* Search Input */}
            <div className="mb-3">
                <input
                    className="form-control"
                    type="text"
                    placeholder="Search users..."
                    maxLength={25}
                    minLength={1}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (/^[a-zA-Z\s]*$/.test(value)) {
                            setSearchTerm(value);
                        }
                    }}
                />
            </div>

            {/* User List */}
            <ul className="list-group mb-3">
                <li style={{ listStyleType: "none" }}>
                    <div className="d-flex flex-row justify-space-even mb-2">
                        <div className="w-100 text-center">
                            <strong>username</strong>
                        </div>
                        <div className="w-100 text-center">
                            <strong>creation time</strong>
                        </div>
                        <div className="w-100 text-center">
                            <strong>status</strong>
                        </div>
                        <div className="w-100 text-center">
                            <strong>action</strong>
                        </div>
                    </div>
                </li>
                {users.map((user) => (
                    <li
                        key={user.user}
                        className="list-group-item"
                        style={{ minHeight: "72px" }}
                    >
                        <div className="d-flex flex-row justify-space-even align-items-center">
                            <div className="w-100 text-center">
                                <strong>{user.user}</strong>
                            </div>
                            <div className="w-100 text-center">
                                <strong>{user.created}</strong>
                            </div>
                            <div
                                className={`w-100 text-center ${user.status === "active"
                                    ? "text-green"
                                    : "text-red"
                                    }`}
                            >
                                <strong>{user.status}</strong>
                            </div>
                            <div className="w-100 text-center">
                                <button
                                    className={`btnPrimary ${user.status === "active"
                                        ? "bg-red"
                                        : "bg-green"
                                        }`}
                                    onClick={() =>
                                        toggleUserStatus(user.user, user.status)
                                    }
                                >
                                    {user.status === "active"
                                        ? "Click to deactivate"
                                        : "Click to activate"}
                                </button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            {/* Skeleton Loader */}
            {loading && (
                <div className="mb-3">
                    {[...Array(USERS_PER_PAGE)].map((_, i) => (
                        <div
                            key={i}
                            className="list-group-item placeholder-glow"
                        >
                            <span className="placeholder col-6"></span>
                        </div>
                    ))}
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="text-danger text-center mb-3">
                    {error}
                    <br />
                    <button
                        className="btn btn-sm btn-outline-dark mt-2"
                        onClick={handleRetry}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Load More Button */}
            {!loading && hasMore && (
                <button
                    className="btn btn-load"
                    onClick={() => setPage((prev) => prev + 1)}
                >
                    Load More
                </button>
            )}

            {!hasMore && users.length > 0 && !loading && (
                <div className="text-muted">All users loaded</div>
            )}

            {!loading && users.length === 0 && !error && (
                <div className="text-muted">No users found</div>
            )}
        </div>
    );
}
